/**
 * Gemeinsame Typen, Grenzen und Berechnungen für Abstimmungen (§10, §11, §15, §18).
 *
 * Nur Konstanten, Typen und reine Funktionen (§41.2). Die Feldlängen stehen hier, damit Formular
 * und Server dieselben Grenzen verwenden — verbindlich geprüft wird serverseitig (§25.2).
 *
 * Dieses Modul enthält bewusst **keine** Funktion, die aus Einzelstimmzetteln etwas berechnet: die
 * Auszählung geschieht ausschließlich serverseitig beim Schließen (§14.3, §16).
 */
/** Status einer Abstimmung (§10.3). */
export const ABSTIMMUNGSSTATUS_WERTE = [
    'entwurf',
    'bereit',
    'geoeffnet',
    'geschlossen',
    'annulliert',
];
/** Beschriftungen für die Oberfläche (§37). */
export const ABSTIMMUNGSSTATUS_BESCHRIFTUNG = {
    entwurf: 'Entwurf',
    bereit: 'Bereit',
    geoeffnet: 'Geöffnet',
    geschlossen: 'Geschlossen',
    annulliert: 'Annulliert',
};
/** Auswahlarten (§11.3, §11.4). Stufe S3 setzt ausschließlich `einfach` um. */
export const AUSWAHLART_WERTE = ['einfach', 'mehrfach'];
/** Regel bei Mehrfachauswahl (§11.4). */
export const AUSWAHLREGEL_WERTE = ['bis_zu', 'genau'];
/** Art einer Auswahlmöglichkeit (§11.1, §11.2). */
export const OPTIONSART_WERTE = ['regulaer', 'nein', 'enthaltung'];
/** Beschriftungen der Sonderoptionen (§11.2). Ihr Text ist nicht frei wählbar. */
export const SONDEROPTION_BESCHRIFTUNG = {
    nein: 'Nein',
    enthaltung: 'Enthaltung',
};
/** Feldlängen und Mengengrenzen nach §10.1, §11.1, §11.2 und §17.3. */
export const ABSTIMMUNG_GRENZEN = {
    ueberschrift: 200,
    text: 10000,
    optionstext: 250,
    /** Sonderoptionen zählen nicht mit; sichtbar sind damit höchstens 18 (§11.2). */
    regulaereOptionen: 16,
    annullierungsgrund: 1000,
    ergebnisfeststellung: 1000,
};
/** Mindestzahl auswählbarer Möglichkeiten bei Einfachauswahl (§11.3). */
export const MIN_AUSWAHLMOEGLICHKEITEN = 2;
/**
 * Prozentualer Anteil einer Option (§18.2).
 *
 * Bezugsgröße sind die abgegebenen Stimmzettel, nicht die berücksichtigten Teilnehmer. Gilt auch
 * für „Nein" und „Enthaltung". Gerundet auf eine Nachkommastelle.
 *
 * Liegt an einer einzigen Stelle, damit Oberfläche, PDF und CSV (S6) nicht auseinanderlaufen.
 */
export function prozentAnteil(stimmen, abgegebeneStimmen) {
    if (abgegebeneStimmen <= 0) {
        return 0;
    }
    return Math.round((stimmen / abgegebeneStimmen) * 1000) / 10;
}
/** Darstellung eines Prozentwerts mit einer Nachkommastelle (§18.2, §37). */
export function formatiereProzent(wert) {
    return `${wert.toFixed(1).replace('.', ',')} %`;
}
/** Ob eine Abstimmung in diesem Status noch bearbeitet werden darf (§10.3). */
export function istAbstimmungBearbeitbar(status) {
    return status === 'entwurf';
}
/** Ob eine Abstimmung in diesem Status gelöscht werden darf (§10.2, §10.3). */
export function istAbstimmungLoeschbar(status) {
    return status === 'entwurf' || status === 'bereit';
}
/**
 * Beschreibt die Auswahlregel für die Teilnehmeroberfläche (§19.2).
 *
 * Die Regel muss dort sichtbar sein, damit klar ist, was erwartet wird.
 */
export function beschreibeAuswahlregel(auswahlart, stimmenanzahl, auswahlregel) {
    if (auswahlart === 'einfach') {
        return 'Bitte genau eine Möglichkeit auswählen.';
    }
    const anzahl = stimmenanzahl ?? 1;
    return auswahlregel === 'genau'
        ? `Bitte genau ${anzahl} Möglichkeiten auswählen.`
        : `Bitte bis zu ${anzahl} Möglichkeiten auswählen.`;
}
export const VORLAGEN = [
    {
        name: 'Beschluss',
        beschreibung: 'Zustimmung, Ablehnung und Enthaltung zu einem Antrag.',
        eingabe: {
            ueberschrift: '',
            text: '',
            auswahlart: 'einfach',
            stimmenanzahl: null,
            auswahlregel: null,
            sonderoptionNein: true,
            sonderoptionEnthaltung: true,
            optionen: ['Ja'],
        },
    },
    {
        name: 'Personenwahl',
        beschreibung: 'Eine Person aus mehreren zur Wahl stehenden.',
        eingabe: {
            ueberschrift: '',
            text: '',
            auswahlart: 'einfach',
            stimmenanzahl: null,
            auswahlregel: null,
            sonderoptionNein: false,
            sonderoptionEnthaltung: true,
            optionen: ['', '', ''],
        },
    },
    {
        name: 'Mehrfachwahl',
        beschreibung: 'Mehrere Personen oder Punkte, bis zu drei Stimmen.',
        eingabe: {
            ueberschrift: '',
            text: '',
            auswahlart: 'mehrfach',
            stimmenanzahl: 3,
            auswahlregel: 'bis_zu',
            sonderoptionNein: false,
            sonderoptionEnthaltung: true,
            optionen: ['', '', '', ''],
        },
    },
    {
        name: 'Freie Abstimmung',
        beschreibung: 'Leere Vorlage ohne Vorbelegung.',
        eingabe: {
            ueberschrift: '',
            text: '',
            auswahlart: 'einfach',
            stimmenanzahl: null,
            auswahlregel: null,
            sonderoptionNein: false,
            sonderoptionEnthaltung: false,
            optionen: ['', ''],
        },
    },
];
//# sourceMappingURL=abstimmung.js.map